import React, { useState, useEffect } from 'react';
import axios from 'axios';
import WorkflowSlugEditor from './WorkflowSlugEditor';
import NotificationModal from '../shared/NotificationModal';
import ConfirmModal from '../shared/ConfirmModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface SlugWorkflow {
  id: number;
  name: string;
  workflow_slug: string;
  category: string;
  tags: string[];
  override_count: number;
  created_at: string;
  updated_at: string;
}

const SlugWorkflowsList: React.FC = () => {
  const [workflows, setWorkflows] = useState<SlugWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [initialWorkflowName, setInitialWorkflowName] = useState<string>('');
  const [initialWorkflowSlug, setInitialWorkflowSlug] = useState<string>('');

  // Notification modal state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'success' | 'error' | 'info'>('info');

  // Confirm modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteWorkflowId, setDeleteWorkflowId] = useState<number | null>(null);
  const [deleteWorkflowName, setDeleteWorkflowName] = useState<string>('');

  // Missing slugs state
  const [missingBaseWorkflows, setMissingBaseWorkflows] = useState<string[]>([]);
  const [loadingMissingSlugs, setLoadingMissingSlugs] = useState(false);

  useEffect(() => {
    fetchWorkflows();
    fetchMissingSlugs();
  }, []);

  const fetchMissingSlugs = async () => {
    try {
      setLoadingMissingSlugs(true);
      const token = localStorage.getItem('token');
      const response = await axios.get<{ missing_base_workflows: string[] }>(
        `${API_BASE}/workflows/missing-slugs`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMissingBaseWorkflows(response.data.missing_base_workflows);
    } catch (error: any) {
      console.error('Failed to fetch missing slug workflows:', error);
    } finally {
      setLoadingMissingSlugs(false);
    }
  };

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/workflows`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filter only slug-type workflows
      const slugWorkflows = response.data.filter((w: any) => w.workflow_type === 'slug');
      setWorkflows(slugWorkflows);
    } catch (error: any) {
      console.error('Failed to fetch slug workflows:', error);
      setNotificationMessage(error.response?.data?.error || 'Failed to fetch slug workflows');
      setNotificationType('error');
      setShowNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (workflowId: number, workflowName: string) => {
    setDeleteWorkflowId(workflowId);
    setDeleteWorkflowName(workflowName);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteWorkflowId) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/workflows/${deleteWorkflowId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowConfirmModal(false);
      setNotificationMessage('Slug workflow deleted successfully!');
      setNotificationType('success');
      setShowNotification(true);
      fetchWorkflows();
    } catch (error: any) {
      console.error('Failed to delete workflow:', error);
      setShowConfirmModal(false);
      setNotificationMessage(error.response?.data?.error || 'Failed to delete workflow');
      setNotificationType('error');
      setShowNotification(true);
    }
  };

  const handleCreateNew = () => {
    setSelectedWorkflowId(null);
    setInitialWorkflowName('');
    setInitialWorkflowSlug('');
    setShowEditor(true);
  };

  const handleCreateFromMissing = (filename: string) => {
    const nameWithoutExtension = filename.replace('.json', '');
    setSelectedWorkflowId(null);
    setInitialWorkflowName(nameWithoutExtension);
    setInitialWorkflowSlug(nameWithoutExtension);
    setShowEditor(true);
  };

  const handleEdit = (workflowId: number) => {
    setSelectedWorkflowId(workflowId.toString());
    setInitialWorkflowName('');
    setInitialWorkflowSlug('');
    setShowEditor(true);
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setSelectedWorkflowId(null);
    setInitialWorkflowName('');
    setInitialWorkflowSlug('');
    fetchWorkflows();
    fetchMissingSlugs(); // Refresh missing slugs after save
  };

  const filteredWorkflows = workflows.filter(w =>
    w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.workflow_slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading slug workflows...</div>
      </div>
    );
  }

  // If showing editor, render it instead
  if (showEditor) {
    return (
      <WorkflowSlugEditor
        workflowId={selectedWorkflowId || undefined}
        initialName={initialWorkflowName || undefined}
        initialSlug={initialWorkflowSlug || undefined}
        onSave={handleEditorClose}
        onCancel={handleEditorClose}
      />
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Slug Workflows</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage EOK server-side workflows with parameter overrides
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          <span className="text-lg">+</span>
          Create New Slug Workflow
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search workflows..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Missing Slugs Section */}
      {missingBaseWorkflows.length > 0 && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <span>ℹ️</span>
            Base Workflows Without Slug Versions ({missingBaseWorkflows.length})
          </h3>
          <p className="text-sm text-blue-800 mb-3">
            These base workflows from GitHub don't have slug-based versions yet. Create slug workflows for them to enable EOK API integration.
          </p>
          <div className="flex flex-wrap gap-2">
            {missingBaseWorkflows.map((filename) => (
              <button
                key={filename}
                onClick={() => handleCreateFromMissing(filename)}
                className="px-3 py-1.5 bg-white border border-blue-300 rounded-md text-sm font-mono text-blue-900 hover:bg-blue-100 hover:border-blue-400 transition-colors cursor-pointer"
              >
                {filename}
              </button>
            ))}
          </div>
        </div>
      )}

      {loadingMissingSlugs && missingBaseWorkflows.length === 0 && (
        <div className="mb-6 text-sm text-gray-500 italic">
          Analyzing base workflows for missing slug versions...
        </div>
      )}

      {/* Workflows List */}
      {filteredWorkflows.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {searchQuery ? 'No workflows match your search.' : 'No slug workflows found. Create your first one!'}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredWorkflows.map((workflow) => (
            <div
              key={workflow.id}
              className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">⚙️</span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{workflow.name}</h3>
                      <div className="flex items-center gap-4 mt-1">
                        <p className="text-sm text-gray-600">
                          Slug: <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">{workflow.workflow_slug}</code>
                        </p>
                        <p className="text-sm text-gray-600">
                          Category: <span className="font-medium">{workflow.category}</span>
                        </p>
                        <p className="text-sm text-gray-600">
                          Overrides: <span className="font-medium">{workflow.override_count || 0}</span>
                        </p>
                      </div>
                      {workflow.tags && workflow.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {workflow.tags.map((tag) => (
                            <span
                              key={tag}
                              className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(workflow.id)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(workflow.id, workflow.name)}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showNotification}
        onClose={() => setShowNotification(false)}
        message={notificationMessage}
        type={notificationType}
      />

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={confirmDelete}
        onCancel={() => setShowConfirmModal(false)}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${deleteWorkflowName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default SlugWorkflowsList;
